/* eslint-disable no-nested-ternary */
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { v4 as UUIDv4 } from 'uuid';
import { chatApi } from 'api/chat';
import { Button } from 'components/_new/Button';
import { ChatImage, ChatMessage, ChatThreadUnknown, ChatThreadWithMessage } from 'types/Chat';
import { Spinner } from 'components/_new/Spinner';
import { classNames } from 'primereact/utils';
import { dateFormat } from 'utils/dateFormat';
import { Avatar } from 'components/_new/Avatar';
import { userDisplayName } from 'utils/userDisplayName';
import { User } from 'types/User';
import { useAuth } from 'contexts/authContext';
import { Icon } from 'components/_new/Icon';
import { useIsWindowFocused } from 'hooks/useWindowFocus';
import { useLocalStorage } from '@uidotdev/usehooks';
import { GalleryImage } from 'components/_new/ImageGalleryViewer/ImageGalleryViewer';
import { useChat } from './useChat';
import { useChatConversationSubscriber } from './useChatConversationSubscriber';
import { useChatThreadSubscriber } from './useChatSubscrber';
import CustomConfirmDialog from '../_common/ConfirmDialog';
import { useChatContext } from './ChatContextProvider';
import { ChatImageGrid } from './ChatImageGrid';
import { ChatMessageComposer } from './ChatMessageComposer';
import { useChatImageUploads } from './useChatImageUploads';

type ChatMessageLocal = ChatMessage & {
	sending?: boolean;
	localId?: string;
	error?: boolean;
	deleted?: boolean;
	deletedText?: string;
	images: ChatImage[];
};

type MessageDeletedEvent = {
	event: 'message-deleted';
	data: {
		id: number;
		threadId: number;
		deleted: boolean;
		deletedAt: Date;
		softDelete: boolean;
		authorProgramMembershipId: number;
	};
};

type ChatEventData = ChatMessageLocal | MessageDeletedEvent;

type ChatMessageCloudProps = {
	your: boolean;
	author: User;
	message: string;
	images?: ChatImage[];
	galleryImages?: GalleryImage[];
	createdAt: Date;
	readedAt: Date | null;
	sending?: boolean;
	deleted?: boolean;
	deletedText?: string;
	onRetry?: () => void;
	onDelete?: () => void;
	canDelete?: boolean;
};

const ChatMessageCloud = ({
	your,
	author,
	message,
	images = [],
	galleryImages = [],
	createdAt,
	readedAt,
	sending = false,
	deleted = false,
	deletedText,
	onRetry = undefined,
	onDelete = undefined,
	canDelete = false,
}: ChatMessageCloudProps) => {
	const { t } = useTranslation();

	const prepareDate = (date: Date) => {
		const dayDiff = dayjs().diff(date, 'day');
		if (dayDiff === 0) {
			return dayjs(createdAt).format('HH:mm');
		}
		return dateFormat(date, 'full');
	};

	const renderState = () => {
		if (your) {
			if (sending) {
				return (
					<span
						title={t('chat.sending')}
						className="flex justify-content-center align-items-center w-1rem h-1rem"
					>
						<Spinner size="xs" />
					</span>
				);
			}
			if (onRetry) {
				return (
					<>
						<span
							className="flex justify-content-center align-items-center w-1rem h-1rem text-error"
							title={t('chat.sendError')}
						>
							<Icon name="times-circle" />
						</span>
						<Button
							label={t('chat.retry')}
							variant="danger-text"
							variantSize="xs"
							noPad
							onClick={onRetry}
						/>
					</>
				);
			}
			return (
				<span
					className="flex justify-content-center align-items-center w-1rem h-1rem"
					title={readedAt ? `${t('chat.readedAt')} ${readedAt.toLocaleString()}` : t('chat.notReaded')}
				>
					<Icon name="circle-check" iconStyle={readedAt ? 'solid' : 'regular'} />
				</span>
			);
		}
		return null;
	};

	const renderDeleteButton = () => {
		if (your && canDelete && !sending && !deleted) {
			return (
				<Button
					icon="trash"
					variant="danger-text"
					variantSize="xs"
					noPad
					onClick={onDelete}
					title={t('chat.deleteMessage')}
					className="opacity-70 hover:opacity-100"
					iconOnly
					label={t('actions.remove')}
					circled
				/>
			);
		}
		return null;
	};

	const renderMessageWithLinks = (text: string | undefined) => {
		if (!text) return '';
		const urlRegex = /(https?:\/\/[^\s]+)/g;
		return text.split(urlRegex).map((part, index) => {
			if (urlRegex.test(part)) {
				return (
					<a
						// eslint-disable-next-line react/no-array-index-key
						key={index}
						href={part}
						target="_blank"
						rel="noopener noreferrer"
						className="text-primary underline"
						style={{ wordBreak: 'break-all' }}
					>
						{part}
					</a>
				);
			}
			return part;
		});
	};

	const displayMessage = deleted ? deletedText || t('chat.messageDeleted') : message;
	const messageClassName = deleted ? 'text-gray italic' : '';

	return (
		<div className="chat-message gap-2">
			<div className="py-1">
				<Avatar id={author.id} src={author.avatar} name={userDisplayName(author, null)} />
			</div>
			<div
				className={classNames('chat-message-cloud', {
					'chat-message-interlocuter': !your,
					'chat-message-your': your,
					'chat-message-new': !your && !readedAt,
					'chat-message-deleted': deleted,
				})}
			>
				{images.length > 0 ? <ChatImageGrid images={images} galleryImages={galleryImages} /> : null}
				<div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} className={messageClassName}>
					{renderMessageWithLinks(displayMessage)}
				</div>
				<div
					className="flex flex-row gap-1 chat-message-date align-items-center"
					title={dateFormat(createdAt, 'full')}
				>
					<span>{prepareDate(createdAt)}</span>
					{renderState()}
					{renderDeleteButton()}
				</div>
			</div>
		</div>
	);
};

type ChatConversationWindowProps = {
	thread: ChatThreadUnknown;
};

export const ChatConversationWindow = ({ thread }: ChatConversationWindowProps) => {
	const { t } = useTranslation();

	const [chatDrafts, setChatDrafts] = useLocalStorage<
		Record<string, { message: string; messageUuid: string; images: ChatImage[] }>
	>('chat-drafts', {});

	const { currentUser } = useAuth();
	const { currentProgramMembershipId } = useChatContext();
	const { queryUniqueParam, readThread } = useChat();

	const threadExists = Boolean(thread.id);
	const draftKey = thread.id ? String(thread.id) : `new-${thread.interlocutorProgramMembershipId}`;
	const [localMessages, setLocalMessages] = useState<ChatMessageLocal[]>([]);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [messageToDelete, setMessageToDelete] = useState<number | null>(null);
	const [messageUuid, setMessageUuid] = useState(UUIDv4());

	const messageWindowRef = useRef<HTMLDivElement>(null);
	const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);

	const scrollToBottom = useCallback(() => {
		if (messageWindowRef.current && autoScrollEnabled) {
			messageWindowRef.current.scrollTo(0, messageWindowRef.current.scrollHeight);
		}
	}, [autoScrollEnabled]);

	useEffect(() => {
		const handleScroll = (event: any) => {
			const { scrollHeight, scrollTop, clientHeight } = event.target;
			const positionFromBottom = scrollHeight - scrollTop - clientHeight;
			setAutoScrollEnabled(positionFromBottom < 50);
			if (positionFromBottom < 50) {
				setShowNewMessageButton(false);
			}
		};
		messageWindowRef.current?.addEventListener('scroll', handleScroll);
		return () => {
			// eslint-disable-next-line react-hooks/exhaustive-deps
			messageWindowRef.current?.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const {
		isFetching: messagesFetching,
		fetchNextPage: messagesFetch,
		hasNextPage: messagesHasNext,
		isLoading: messagesLoading,
		isFetched: messagesFetched,
	} = useInfiniteQuery<ChatMessage[]>(
		[
			'chat-messages',
			{ interlocutorProgramMembershipId: thread.interlocutorProgramMembershipId, ...queryUniqueParam },
		],
		({ pageParam }) => {
			return chatApi.getThreadMessages(currentProgramMembershipId!, Number(thread.id), pageParam);
		},
		{
			enabled: localMessages.length === 0 && Boolean(currentProgramMembershipId) && threadExists,
			getNextPageParam: (results) => (results.length < 10 ? undefined : results[results.length - 1].id || 0),
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			refetchOnMount: true,
			refetchOnReconnect: false,
			onSuccess: (data) => {
				const serverMessages = (data?.pages || [])
					.flat()
					.sort(({ id: idA }, { id: idB }) => (idA > idB ? 1 : -1));
				setLocalMessages((prev) => (prev.length === 0 ? serverMessages : prev));
				setTimeout(() => {
					scrollToBottom();
				}, 250);
			},
		}
	);

	useEffect(() => {
		scrollToBottom();
	}, [scrollToBottom, localMessages]);

	useEffect(() => {
		localMessages.map((m) => ({ id: m.id, deleted: m.deleted, message: m.message?.substring(0, 20) }));
	}, [localMessages]);

	const localReadThread = useCallback(() => {
		readThread(thread.interlocutorProgramMembershipId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [thread.interlocutorProgramMembershipId, messagesFetched]);

	const [messageLocalID, setMessageLocalID] = useState(0);
	const { handleSubmit, reset, watch, setValue } = useForm({ defaultValues: { message: '' } });
	const currentMessage = watch('message');

	const {
		images: draftImages,
		validationMessage,
		addFiles,
		cancelUpload,
		retryUpload,
		removeImage,
		resetState: resetDraftImages,
		restoreUploadedImages,
		uploadedImages,
		uploadedImageIds,
		isUploading,
	} = useChatImageUploads({
		programMembershipId: currentProgramMembershipId,
		interlocutorProgramMembershipId: Number(thread.interlocutorProgramMembershipId),
		messageUuid,
	});

	const { mutate, isLoading } = useMutation(
		(data: { message: string; localId: string; messageUuid: string; imageIds: number[] }) =>
			chatApi.sendChatMessage(currentProgramMembershipId!, thread.interlocutorProgramMembershipId, data),
		{
			onMutate: ({ message, localId }) => {
				setMessageLocalID((prev) => prev + 1);
				const sendingMessage: ChatMessageLocal = {
					id: 0,
					authorProgramMembershipId: currentProgramMembershipId,
					createdAt: new Date(),
					message,
					images: uploadedImages,
					readedAt: null,
					sending: true,
					error: false,
					localId,
				} as ChatMessageLocal;
				setLocalMessages((prev) => [...prev.filter((msg) => msg.localId !== localId), sendingMessage]);
				reset({ message: '' });
				scrollToBottom();
				setChatDrafts((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== draftKey)));
				resetDraftImages();
				setMessageUuid(UUIDv4());
			},
			onSuccess: (data: ChatMessageLocal) => {
				setLocalMessages((prev) =>
					prev.map((message) =>
						message.localId === data.localId ? { ...message, ...data, sending: false } : message
					)
				);
			},
			onError: (response, { localId }) => {
				setLocalMessages((prev) =>
					prev.map((message) =>
						message.localId === localId ? { ...message, sending: false, error: true } : message
					)
				);
			},
		}
	);

	const { mutate: deleteMessage } = useMutation(
		(messageId: number): Promise<{ success: boolean; wasSentAsReminder: boolean; wasRead: boolean }> =>
			chatApi.deleteMessage(currentProgramMembershipId!, messageId),
		{
			onMutate: (messageId) => {
				setLocalMessages((prev) =>
					prev.map((message) => {
						if (message.id === messageId) {
							const shouldShowDeletedText = message.readedAt; // Check if message was read
							return shouldShowDeletedText
								? { ...message, deleted: true, deletedText: t('chat.messageDeleted') }
								: { ...message, deleted: true };
						}
						return message;
					})
				);
			},
			onSuccess: (response: { success: boolean; wasSentAsReminder: boolean; wasRead: boolean }, messageId) => {
				setLocalMessages((prev) => {
					const updated = prev
						.map((message) => {
							if (message.id === messageId) {
								if (response.wasRead || response.wasSentAsReminder) {
									return {
										...message,
										deleted: true,
										deletedText: t('chat.messageDeleted'),
									};
								}
								return null;
							}
							return message;
						})
						.filter(Boolean) as ChatMessageLocal[];
					return updated;
				});
			},
			onError: (error: any, messageId) => {
				setLocalMessages((prev) =>
					prev.map((message) => {
						if (message.id === messageId) {
							return { ...message, deleted: false, deletedText: undefined };
						}
						return message;
					})
				);
			},
		}
	);

	const prepareAndMutate = (message: string, localId?: string) => {
		if ((message && message.trim().length > 0) || uploadedImageIds.length > 0) {
			mutate({
				message,
				localId: localId || `${thread.id}-${messageLocalID}`,
				messageUuid,
				imageIds: uploadedImageIds,
			});
		}
	};

	const handleDeleteMessage = useCallback((messageId: number) => {
		setMessageToDelete(messageId);
		setDeleteConfirmOpen(true);
	}, []);

	const confirmDeleteMessage = useCallback(() => {
		if (messageToDelete) {
			deleteMessage(messageToDelete);
		}
		setDeleteConfirmOpen(false);
		setMessageToDelete(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deleteMessage, messageToDelete, currentUser, localMessages, currentProgramMembershipId]);

	const cancelDeleteMessage = useCallback(() => {
		setDeleteConfirmOpen(false);
		setMessageToDelete(null);
	}, []);

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && e.ctrlKey) {
			e.preventDefault();
			const message = watch('message');
			if ((message && message.trim().length > 0) || uploadedImageIds.length > 0) {
				prepareAndMutate(message);
			}
		}
	};

	const handleSubmitForm = handleSubmit(({ message }) => {
		if ((message && message.trim().length > 0) || uploadedImageIds.length > 0) {
			prepareAndMutate(message);
		}
	});

	useEffect(() => {
		if (threadExists) {
			localReadThread();
		}
	}, [thread, threadExists, messagesFetched, localReadThread]);

	const windowFocus = useIsWindowFocused();
	const [readAfterWindowFocus, setReadAfterWindowFocus] = useState(false);
	useEffect(() => {
		if (readAfterWindowFocus && autoScrollEnabled && windowFocus) {
			localReadThread();
			setReadAfterWindowFocus(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [readAfterWindowFocus, thread, windowFocus, autoScrollEnabled]);

	const handleNewChatMessage = useCallback(
		(data: ChatEventData) => {
			if (typeof data === 'object' && 'event' in data && data.event === 'message-deleted') {
				const deletedMessageData = data.data;
				setLocalMessages((prev) => {
					const updated = prev
						.map((message) => {
							if (message.id === deletedMessageData.id) {
								if (deletedMessageData.softDelete) {
									return {
										...message,
										deleted: true,
										deletedText: t('chat.messageDeleted'),
									};
								}
								return null; // Will be filtered out
							}
							return message;
						})
						.filter(Boolean) as ChatMessageLocal[];
					return updated;
				});
				return;
			}

			// Regular message handling - now we know data is ChatMessageLocal
			const messageData = data as ChatMessageLocal;

			// only for current thread
			if (thread.id === messageData.threadId) {
				// message from interlocutor
				if (messageData.authorProgramMembershipId !== currentProgramMembershipId!) {
					if (!autoScrollEnabled) {
						setShowNewMessageButton(true);
					}
					if (windowFocus && autoScrollEnabled) {
						localReadThread();
					} else {
						setReadAfterWindowFocus(true);
					}
					setLocalMessages((prev) => {
						const updated = [...prev, messageData];
						return updated;
					});
				} else {
					setLocalMessages((prev) =>
						prev.map((msg) => {
							if (msg.localId === messageData?.localId) {
								return { ...messageData, sending: false };
							}
							return msg;
						})
					);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			thread.id,
			thread.interlocutorProgramMembershipId,
			currentProgramMembershipId,
			autoScrollEnabled,
			windowFocus,
			localReadThread,
		]
	);

	const handeChatReadedInternal = useCallback(
		(data: ChatThreadWithMessage, toSelf = false) => {
			// only for current thread
			if (thread.id === data.id) {
				setLocalMessages((prev) =>
					prev.map((message) => {
						if (
							((toSelf && message.authorProgramMembershipId !== currentProgramMembershipId!) ||
								(!toSelf && message.authorProgramMembershipId === currentProgramMembershipId!)) &&
							message.readedAt === null
						) {
							return {
								...message,
								readedAt: data.interlocutorReadedAt ? new Date(data.interlocutorReadedAt) : new Date(),
							};
						}
						return message;
					})
				);
			}
		},
		[currentProgramMembershipId, thread.id]
	);
	const handeChatReadedInterlocutorEvent = useCallback(
		({ detail }: CustomEvent<ChatThreadWithMessage>) => handeChatReadedInternal(detail, false),
		[handeChatReadedInternal]
	);
	const handeChatReadedSelfEvent = useCallback(
		({ detail }: CustomEvent<ChatThreadWithMessage>) => handeChatReadedInternal(detail, true),
		[handeChatReadedInternal]
	);

	useChatConversationSubscriber({
		interlocutorMembershipId: Number(thread.interlocutorProgramMembershipId),
		onMessage: handleNewChatMessage as (data: ChatMessageLocal) => void,
	});

	useChatThreadSubscriber({
		onChatThread: messagesFetched ? handeChatReadedInterlocutorEvent : undefined,
		onChatThreadSelf: messagesFetched ? handeChatReadedSelfEvent : undefined,
	});

	const showNoChats = !threadExists && !localMessages.length;
	const showLoadingMessages = threadExists && messagesLoading;

	const [showNewMessageButton, setShowNewMessageButton] = useState(false);
	const handleGoToNewMessageClick = useCallback(() => {
		scrollToBottom();
		setAutoScrollEnabled(true);
		setShowNewMessageButton(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setLocalMessages([]);
		const currentDraft = chatDrafts[draftKey];
		if (currentDraft?.message) {
			setValue('message', currentDraft.message);
			setMessageUuid(currentDraft.messageUuid || UUIDv4());
			restoreUploadedImages(currentDraft.images || []);
		} else {
			setValue('message', '');
			setMessageUuid(UUIDv4());
			resetDraftImages();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [draftKey]);

	const conversationGalleryImages = useMemo(
		() =>
			localMessages.flatMap((message) => {
				const author =
					message.authorProgramMembershipId === currentProgramMembershipId
						? (currentUser as unknown as User)
						: thread.interlocutorProgramMembership.user;

				return (message.images || []).map((image): GalleryImage => ({
					id: image.id,
					thumbUrl: image.thumbUrl,
					imageUrl: image.imageUrl,
					authorName: userDisplayName(author),
					authorAvatar: author.avatar,
					sentAt: message.createdAt,
					originalFileName: image.originalFileName,
					fileSize: image.fileSize,
				}));
			}),
		[currentProgramMembershipId, currentUser, localMessages, thread.interlocutorProgramMembership.user]
	);

	useEffect(() => {
		const trimmedMessage = (currentMessage || '').trim();
		if (trimmedMessage.length === 0 && draftImages.length === 0) {
			setChatDrafts((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== draftKey)));
			return;
		}

		setChatDrafts((prev) => ({
			...prev,
			[draftKey]: {
				message: currentMessage || '',
				messageUuid,
				images: uploadedImages,
			},
		}));
	}, [currentMessage, draftImages.length, uploadedImages, draftKey, messageUuid, setChatDrafts]);

	return (
		<div className="flex-1 h-full flex flex-column gap-2 overflow-y-hidden p-1">
			<div className="h-full  relative overflow-hidden">
				<div
					ref={messageWindowRef}
					className="flex-1 flex flex-column gap-1 h-full overflow-y-scroll p-styled-scrollbar-purplishblue bg-new-gray rounded-md p-2"
				>
					{(showNoChats || showLoadingMessages) && (
						<div className="h-full flex flex-column align-items-center justify-content-center">
							{showNoChats && (
								<>
									<p className="text-gray">{t('chat.noConversations')}</p>
									<p className="text-gray">{t('chat.writeNewChat')}</p>
								</>
							)}
							{showLoadingMessages && (
								<>
									<Spinner />
									<p className="text-gray">{t('chat.loadingChats')}</p>
								</>
							)}
						</div>
					)}

					{!messagesLoading && messagesHasNext && (
						<Button
							label={t('chat.loadMoreChats')}
							onClick={() => messagesFetch()}
							loading={messagesFetching}
						/>
					)}

					{(localMessages || []).map((msg) => {
						const {
							id,
							authorProgramMembershipId,
							message,
							images,
							createdAt,
							readedAt,
							localId,
							sending = false,
							error = false,
							deleted = false,
							deletedText,
						} = msg;
						const your = authorProgramMembershipId === currentProgramMembershipId!;
						const handleRetry = error ? () => prepareAndMutate(message, localId) : undefined;
						const handleDelete = your && id > 0 ? () => handleDeleteMessage(id) : undefined;

						// Can delete if it's your message, not currently sending, has an ID, and not already deleted
						const canDelete = your && !sending && id > 0 && !deleted && !error;

						return (
							<Fragment key={localId || id}>
								<ChatMessageCloud
									your={your}
									author={
										your
											? (currentUser as unknown as User)
											: thread.interlocutorProgramMembership.user
									}
									message={message}
									images={images}
									galleryImages={conversationGalleryImages}
									createdAt={createdAt}
									readedAt={readedAt}
									sending={sending}
									deleted={deleted}
									deletedText={deletedText}
									onRetry={handleRetry}
									onDelete={handleDelete}
									canDelete={canDelete}
								/>
							</Fragment>
						);
					})}
				</div>
				{showNewMessageButton && (
					<div className="absolute bottom-0 w-full p-4 flex flex-row justify-content-center">
						<Button label={t('chat.goToNewMessage')} onClick={handleGoToNewMessageClick} />
					</div>
				)}
			</div>
			<ChatMessageComposer
				message={currentMessage || ''}
				onMessageChange={(value) => setValue('message', value)}
				onMessageBlur={(value) => setValue('message', value)}
				onSubmit={handleSubmitForm}
				onKeyDown={handleKeyPress}
				onPasteImages={addFiles}
				onSelectImages={addFiles}
				images={draftImages}
				onCancelImage={cancelUpload}
				onRetryImage={retryUpload}
				onRemoveImage={removeImage}
				validationMessage={validationMessage}
				sendDisabled={
					isUploading || ((currentMessage || '').trim().length === 0 && uploadedImageIds.length === 0)
				}
				sendLoading={isLoading}
				placeholder={t('chat.writeMessage')}
				sendLabel={t('actions.send')}
			/>

			<CustomConfirmDialog
				header={t('chat.confirmDeleteTitle')}
				message={t('chat.confirmDeleteMessage')}
				confirmationOpen={deleteConfirmOpen}
				setConfirmationOpen={setDeleteConfirmOpen}
				handleAccept={confirmDeleteMessage}
				handleReject={cancelDeleteMessage}
			/>
		</div>
	);
};
